// Sementes de um banco novo: o Perfil "Administrador" com acesso total, as
// Categorias-semente e os vocabulários padrão. Pura e **idempotente** — pode
// rodar em todo boot. Usada pelo app (src/app/main.ts) e pelo servidor
// (server/main.ts) para que os dois nasçam iguais.

import { newId } from "./ids.ts";
import { MODULOS } from "./schema.ts";
import { defaultCategorias } from "./categorias.ts";
import { garantirCategoriaFaturamento } from "./categorias-financeiras.ts";
import { temGravacaoEmConfiguracao } from "./perfis.ts";
import { garantirMotivoRelavagem, listasPadrao } from "./listas.ts";
import type { Snapshot } from "./snapshot.ts";

/**
 * "Dano" deixou de ser Motivo de Retenção (Fase 5): Peça danificada é
 * divergência com motivo, não retenção — como retenção ela entraria no
 * Sublote e somaria no Backlog para sempre. `aplicarSementes` só popula as
 * listas em banco nunca semeado, então sem este passo produção continuaria
 * oferecendo o valor. Devolve o MESMO objeto quando não há nada a fazer.
 */
function removerDanoDosMotivosDeRetencao(snapshot: Snapshot): Snapshot {
  if (!snapshot.listas.motivosRetencao.includes("Dano")) return snapshot;
  return {
    ...snapshot,
    listas: {
      ...snapshot.listas,
      motivosRetencao: snapshot.listas.motivosRetencao.filter((m) => m !== "Dano"),
    },
  };
}

/**
 * `motivosDivergencia` (ADR-0021) é vocabulário novo: banco nunca semeado o
 * recebe no bloco acima, mas banco JÁ semeado — o de produção, por exemplo —
 * pulava aquele bloco e ficava com a lista vazia, sem acesso à Conferida nem
 * à Perda. Espelha `removerDanoDosMotivosDeRetencao` na direção oposta:
 * preenche em vez de remover. Só age quando a lista está **vazia** — não
 * valor a valor —, porque a lista é editável (ADR-0021) e um administrador
 * pode legitimamente remover um dos três valores-semente; valor a valor
 * ressuscitaria essa remoção a cada boot. Vazia é o sinal inequívoco de
 * "nunca inicializada": uma vez populada, por semente ou por edição manual,
 * este passo não mexe mais nela. Devolve o MESMO objeto quando não há nada a
 * fazer.
 */
function preencherMotivosDivergenciaVazios(snapshot: Snapshot): Snapshot {
  if (snapshot.listas.motivosDivergencia.length > 0) return snapshot;
  return {
    ...snapshot,
    listas: {
      ...snapshot.listas,
      motivosDivergencia: listasPadrao().motivosDivergencia,
    },
  };
}

export function aplicarSementes(snapshot: Snapshot): Snapshot {
  let s = snapshot;

  // Banco nunca semeado (sem Perfil e sem Categoria): cria tudo de uma vez.
  if (s.perfis.length === 0 && s.categorias.length === 0) {
    const adminId = newId();
    s = {
      ...s,
      perfis: [{ id: adminId, nome: "Administrador", ativo: true }],
      permissoesModulo: MODULOS.map((modulo) => ({
        perfilId: adminId,
        modulo,
        nivel: "ambos" as const,
      })),
      categorias: defaultCategorias(),
      listas: listasPadrao(),
    };
  }

  // A Categoria Financeira de sistema que a Fatura usa na Conta a Receber que
  // gera (ADR-0011). Semeada à parte do bloco acima porque precisa aparecer
  // também em banco JÁ semeado — o de produção, por exemplo.
  s = garantirCategoriaFaturamento(s);

  // Usuário sem Perfil (ex.: criado pelo bootstrap do servidor, ou vindo de um
  // banco importado) recebe um Perfil administrativo — senão não enxerga
  // nada. Achado por quem TEM gravação em Configuração, não pelo nome
  // "Administrador" (issue #71: a tela de Perfis deixa renomear/inativar
  // qualquer Perfil, e um lookup por nome quebraria em silêncio — o próximo
  // usuário órfão cairia num Perfil qualquer, possivelmente sem acesso
  // nenhum).
  const admin =
    s.perfis.find((p) => p.ativo && temGravacaoEmConfiguracao(s.permissoesModulo, p.id)) ??
    s.perfis.find((p) => p.ativo) ??
    s.perfis[0];
  if (admin && s.usuarios.some((u) => u.perfilId == null)) {
    s = {
      ...s,
      usuarios: s.usuarios.map((u) =>
        u.perfilId == null ? { ...u, perfilId: admin.id } : u,
      ),
    };
  }

  s = removerDanoDosMotivosDeRetencao(s);
  s = preencherMotivosDivergenciaVazios(s);
  // a Taxa de Relavagem conta este motivo — ele sempre existe (ADR-0033)
  s = garantirMotivoRelavagem(s);

  return s;
}
